import React, { useEffect } from "react";
import PropTypes from "prop-types";

import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import PeopleIcon from "@material-ui/icons/People";
import ReportIcon from "@material-ui/icons/Report";
import AssessmentIcon from "@material-ui/icons/Assessment";
import ContactSupportIcon from "@material-ui/icons/ContactSupport";

import UsersTable from "./UsersTable";
import ReportedPlayerTable from "./ReportedPlayerTable";
import SupportTicketsPage from "../support-tickets/SupportTicketsPage";
import MonitoringSection from "../MonitoringSection/MonitoringSection";
import SetELO from "./SetELOModal";
import SetPlayerVerifiedDialog from "./SetPlayerVerifiedDialog";
import SetPlayerNotEligibleDialog from "./SetPlayerNotEligibleDialog";
import EligibilityThreadDialog from "./EligibilityThreadDialog";
import SetPlayerBannedDialog from "./SetPlayerBannedDialog";
import SetPlayerTrackerStatsDialog from "./SetPlayerTrackerStatsDialog";
import SetPlayerMembershipGrantDialog from "./SetPlayerMembershipGrantDialog";
import SetPlayerMembershipRevokeDialog from "./SetPlayerMembershipRevokeDialog";
import SetPlayerFounderBadgeDialog from "./SetPlayerFounderBadgeDialog";
import RebuildPlayerEloDialog from "./RebuildPlayerEloDialog";
import StartPhoneVerificationDialog from "./StartPhoneVerificationDialog";
import IdentityReviewPanel from "./IdentityReviewPanel";
import SendApplicationFeedbackDialog from "./SendApplicationFeedbackDialog";
import ProfileReadinessReviewDialog from "./ProfileReadinessReviewDialog";
import DeleteUserDialog from "./DeleteUserDialog";
import resolveUserSteamId from "./resolveUserSteamId";
import utils from "../../universal/utils";
import adminFetch from "../../universal/adminFetch";
import {
  ADMIN_ROLES,
  canManageAdvancedUserTools,
  canViewDashboard,
  hasAdminRole,
} from "../../universal/adminRoles";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    paddingTop: 16,
    paddingBottom: 16,
    marginTop: 0,

    marginLeft: "auto",
    marginRight: "auto",

    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
  homepageRoot: {
    minHeight: 560,
    marginTop: 8,
  },
  pageTitle: {
    fontSize: "1.8rem",
    fontWeight: 525,
    color: theme.palette.text.primary,
    marginBottom: "2rem",
    letterSpacing: "-0.02em",
    lineHeight: 1.2,
    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
      marginBottom: "1rem",
    },
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    marginTop: '1rem !important',
    marginBottom: '2rem !important',
    overflow: 'hidden',
  },
  tabsRoot: {
    '& .MuiTabs-indicator': {
      background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
      height: 3,
    },
    '& .MuiTabs-flexContainer': {
      gap: '0.5rem',
      padding: '0 1rem',
    },
  },
  tabRoot: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '1rem',
    minHeight: 64,
    color: theme.palette.text.primary,
    transition: 'all 0.3s ease',
    '&:hover': {
      color: "#2563eb",
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
      color: "#2563eb",
    },
    '@media (max-width: 768px)': {
      fontSize: '0.875rem',
      minHeight: 56,
    },
    '@media (max-width: 480px)': {
      fontSize: '0.75rem',
      minHeight: 48,
    },
  },
  containerContentTab: {
    marginTop: '2rem',
  },
  vspacer10: {
    height: 10,
  },
  "@media (min-width: 1024px)": {
    homepageRoot: {
      width: 1400,
      margin: "0 auto",
    },
    lobbyFrame: {
      margin: "20px auto",
      position: "relative",
      padding: "2rem",
      minHeight: 500,
    },
  },
});

const tabs = [
  {
    key: "monitoring",
    label: "Monitoring",
    icon: <AssessmentIcon />,
    allowedAdminRoles: [ADMIN_ROLES.SUPER_ADMIN],
  },
  {
    key: "user-management",
    label: "User Management",
    icon: <PeopleIcon />,
    allowedAdminRoles: [ADMIN_ROLES.REVIEW_ADMIN],
  },
  {
    key: "reported-players",
    label: "Reported Players",
    icon: <ReportIcon />,
    allowedAdminRoles: [ADMIN_ROLES.REVIEW_ADMIN],
  },
  {
    key: "support-tickets",
    label: "Support Tickets",
    icon: <ContactSupportIcon />,
    allowedAdminRoles: [ADMIN_ROLES.REVIEW_ADMIN],
  },
];

function UsersPage(props) {
  const { classes, actions, catalog, history } = props;
  const {
    user,
    users,
    eloDialogState,
    adminUserContext,
    verifyPlayerDialogState,
    notEligiblePlayerDialogState,
    banPlayerDialogState,
    trackerStatsDialogState,
    reportedPlayers,
    mmActivitySummary,
    newestPlayers,
    queueAnalitycsSummary,
    sevenDaysMatchmakingDataForChart,
    thirtyDaysMatchmakingDataForChart,
    ninetyDaysMatchmakingDataForChart,
    sevenDaysQueueDataForChart,
    thirtyDaysQueueDataForChart,
    ninetyDaysQueueDataForChart,
    weeklyDataForChart,
    monthlyDataForChart,
    yearlyDataForChart,
    matchDurationData,
    playersQueuedPerDayData,
    queueEfficiencyPerDayData,
    queueOutcomeDistributionData,
    matchmakingCapacityTrendData,
    serversAliveCount,
  } = catalog;

  const canViewMonitoring = canViewDashboard(user);
  const canUseAdvancedUserTools = canManageAdvancedUserTools(user);
  const visibleTabs = React.useMemo(
    () =>
      tabs.filter((tab) =>
        hasAdminRole(user, tab.allowedAdminRoles)
      ),
    [user]
  );
  const defaultTabKey = canViewMonitoring ? "monitoring" : "user-management";
  const [tabValue, setTabValue] = React.useState(defaultTabKey);
  const [membershipGrantDialogState, setMembershipGrantDialogState] = React.useState(false);
  const [membershipGrantUserContext, setMembershipGrantUserContext] = React.useState({});
  const [membershipRevokeDialogState, setMembershipRevokeDialogState] = React.useState(false);
  const [membershipRevokeUserContext, setMembershipRevokeUserContext] = React.useState({});
  const [founderBadgeDialogState, setFounderBadgeDialogState] = React.useState(false);
  const [founderBadgeUserContext, setFounderBadgeUserContext] = React.useState({});
  const [rebuildPlayerEloDialogState, setRebuildPlayerEloDialogState] = React.useState(false);
  const [rebuildPlayerEloUserContext, setRebuildPlayerEloUserContext] = React.useState({});
  const [startPhoneVerificationDialogState, setStartPhoneVerificationDialogState] =
    React.useState(false);
  const [startPhoneVerificationUserContext, setStartPhoneVerificationUserContext] =
    React.useState({});
  const [identityReviewDialogState, setIdentityReviewDialogState] = React.useState(false);
  const [identityReviewUserContext, setIdentityReviewUserContext] = React.useState({});
  const [identityReviewData, setIdentityReviewData] = React.useState(null);
  const [identityReviewLoading, setIdentityReviewLoading] = React.useState(false);
  const [identityReviewError, setIdentityReviewError] = React.useState("");
  const [applicationFeedbackDialogState, setApplicationFeedbackDialogState] = React.useState(false);
  const [applicationFeedbackUserContext, setApplicationFeedbackUserContext] = React.useState({});
  const [eligibilityThreadDialogState, setEligibilityThreadDialogState] = React.useState(false);
  const [eligibilityThreadUserContext, setEligibilityThreadUserContext] = React.useState({});
  const [readinessReviewDialogState, setReadinessReviewDialogState] = React.useState(false);
  const [readinessReviewUserContext, setReadinessReviewUserContext] = React.useState({});
  const [deleteUserDialogState, setDeleteUserDialogState] = React.useState(false);
  const [deleteUserContext, setDeleteUserContext] = React.useState({});

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === tabValue)) {
      setTabValue(visibleTabs[0]?.key || defaultTabKey);
    }
  }, [defaultTabKey, tabValue, visibleTabs]);

  useEffect(() => {
    if (user) {
      actions.getAllUsers({ userId: user.userId });
      actions.getAllReportedPlayers({ userId: user.userId });
      if (canViewMonitoring) {
        actions.getAdminQueueAnalitycsSummary({ user });
        actions.getAdminMMActivitySummary({ user });
        actions.getAdminNewestPlayers({ user });
        actions.getAdminDaysDataForMatchesChart({ user, days: 7 });
        actions.getAdminDaysDataForQueueChart({ user, days: 7 });
        actions.getAdminDataForSignupsChart({ user, period: "week" });
        actions.getMatchDurationDistribution({ user });
        actions.getAdminPlayersQueuedPerDayData({ user });
        actions.getAdminQueueEfficiencyPerDayData({ user });
        actions.getAdminQueueOutcomeDistributionData({ user });
        actions.getAdminMatchmakingCapacityTrendData({ user, days: 7 });
        actions.getAdminServersAliveCount({ user });
      }
    }
    // The connected container re-binds `actions` on store updates, so keeping it
    // here turns routine rerenders into a full admin refetch loop.
  }, [canViewMonitoring, user]);

  const openSetEloDialog = ({ userId, row }) => {
    actions.setAdminTargetUserContext({ userId, row });
    actions.setELODialogState(true);
  };

  const openSetAsVerifiedDialog = ({ userId, row }) => {
    actions.setAdminTargetUserContext({ userId, row });
    actions.setPlayerVerifiedDialogState(true);
  };

  const openStartPhoneVerificationDialog = ({ userId, row }) => {
    setStartPhoneVerificationUserContext({ userId, row });
    setStartPhoneVerificationDialogState(true);
  };

  const openSetAsNotEligibleDialog = ({ userId, row }) => {
    actions.setAdminTargetUserContext({ userId, row });
    actions.setPlayerNotEligibleDialogState(true);
  };

  const openSetAsBannedDialog = ({ userId, row }) => {
    actions.setAdminTargetUserContext({ userId, row });
    actions.setPlayerBannedDialogState(true);
  };

  const openSetAsTrackerStatsDialog = ({ userId, row }) => {
    actions.setAdminTargetUserContext({ userId, row });
    actions.setPlayerTrackerStatsDialogState(true);
  };

  const openSetMembershipGrantDialog = ({ userId, row }) => {
    setMembershipGrantUserContext({ userId, row });
    setMembershipGrantDialogState(true);
  };

  const openSetMembershipRevokeDialog = ({ userId, row }) => {
    setMembershipRevokeUserContext({ userId, row });
    setMembershipRevokeDialogState(true);
  };

  const openFounderBadgeDialog = ({ userId, row }) => {
    setFounderBadgeUserContext({ userId, row });
    setFounderBadgeDialogState(true);
  };

  const openRebuildPlayerEloDialog = ({ userId, row }) => {
    setRebuildPlayerEloUserContext({ userId, row });
    setRebuildPlayerEloDialogState(true);
  };

  const openReputationOutcomesPage = ({ row }) => {
    const steamId = resolveUserSteamId(row);
    if (!steamId) {
      return;
    }

    history.push(`/admin/reputation-outcomes/${steamId}`);
  };

  const openSendApplicationFeedbackDialog = ({ userId, row }) => {
    setApplicationFeedbackUserContext({ userId, row });
    setApplicationFeedbackDialogState(true);
  };

  const openEligibilityThreadDialog = ({ userId, row }) => {
    setEligibilityThreadUserContext({ userId, row });
    setEligibilityThreadDialogState(true);
  };

  const openReadinessReviewDialog = ({ userId, row }) => {
    setReadinessReviewUserContext({ userId, row });
    setReadinessReviewDialogState(true);
  };

  const openDeleteUserDialog = ({ userId, row }) => {
    setDeleteUserContext({ userId, row });
    setDeleteUserDialogState(true);
  };

  const handleSendApplicationFeedback = async ({ userId, flaggedFields, message }) => {
    const res = await adminFetch(
      `${basePath}/api/v7/admin/users/${userId}/application-feedback/adpass/${catalog.user.userId}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flaggedFields, message }),
      }
    );

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.ok) {
      throw new Error(data.error || data.err || `Request failed (${res.status})`);
    }

    actions.getAllUsers({ userId: catalog.user.userId });
  };

  const loadIdentityReview = React.useCallback(
    async (targetUserId) => {
      if (!user?.userId || !targetUserId) {
        return;
      }

      setIdentityReviewLoading(true);
      setIdentityReviewError("");

      try {
        const res = await adminFetch(
          `${basePath}/api/v7/admin/identity/review/${targetUserId}/adpass/${user.userId}`
        );
        let data = {};
        try {
          data = await res.json();
        } catch (_) {
          data = {};
        }

        if (!res.ok || !data.ok) {
          throw new Error(data.err || data.error || `Request failed (${res.status})`);
        }

        setIdentityReviewData(data.review || null);
      } catch (err) {
        setIdentityReviewError(err.message || "Failed to load identity review");
      } finally {
        setIdentityReviewLoading(false);
      }
    },
    [user]
  );

  const openIdentityReviewDialog = React.useCallback(
    ({ userId, row }) => {
      setIdentityReviewUserContext({ userId, row });
      setIdentityReviewData(null);
      setIdentityReviewError("");
      setIdentityReviewDialogState(true);
      loadIdentityReview(userId);
    },
    [loadIdentityReview]
  );

  const closeIdentityReviewDialog = React.useCallback(() => {
    setIdentityReviewDialogState(false);
    setIdentityReviewError("");
  }, []);

  const refreshIdentityReview = React.useCallback(async () => {
    if (!identityReviewUserContext?.userId || !user?.userId) {
      return;
    }

    await loadIdentityReview(identityReviewUserContext.userId);
    actions.getAllUsers({ userId: user.userId });
  }, [actions, identityReviewUserContext, loadIdentityReview, user]);

  const postIdentityReviewAction = React.useCallback(
    async (path, body = {}) => {
      setIdentityReviewLoading(true);
      setIdentityReviewError("");

      try {
        const res = await adminFetch(path, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        let data = {};
        try {
          data = await res.json();
        } catch (_) {
          data = {};
        }

        if (!res.ok || !data.ok) {
          throw new Error(data.err || data.error || `Request failed (${res.status})`);
        }

        await refreshIdentityReview();
      } catch (err) {
        setIdentityReviewError(err.message || "Identity review action failed");
      } finally {
        setIdentityReviewLoading(false);
      }
    },
    [refreshIdentityReview]
  );

  const handleConfirmIdentityReview = React.useCallback(
    async ({ snapshotId, matchSnapshotId }) => {
      if (!snapshotId || !user?.userId) {
        return;
      }

      await postIdentityReviewAction(
        `${basePath}/api/v7/admin/identity/review/${snapshotId}/confirm/adpass/${user.userId}`,
        { matchSnapshotId }
      );
    },
    [postIdentityReviewAction, user]
  );

  const handleDismissIdentityMatch = React.useCallback(
    async ({ snapshotId, dismissedSnapshotId }) => {
      if (!snapshotId || !dismissedSnapshotId || !user?.userId) {
        return;
      }

      await postIdentityReviewAction(
        `${basePath}/api/v7/admin/identity/review/${snapshotId}/dismiss-match/adpass/${user.userId}`,
        { dismissedSnapshotId }
      );
    },
    [postIdentityReviewAction, user]
  );

  const handleRunIdentityScan = React.useCallback(
    async ({ snapshotId }) => {
      if (!snapshotId || !user?.userId) {
        return;
      }

      await postIdentityReviewAction(
        `${basePath}/api/v7/admin/identity/review/${snapshotId}/scan-now/adpass/${user.userId}`
      );
    },
    [postIdentityReviewAction, user]
  );

  const handleRebuildPlayerElo = async ({ steamId }) => {
    const sid = steamId != null ? String(steamId).trim() : "";
    if (!sid) {
      return { ok: false, error: "This user does not have a Steam ID" };
    }

    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/elo-reconciliation/adpass/${catalog.user.userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mode: "rebuild",
            dryRun: false,
            playerSteamId: sid,
          }),
        }
      );

      let data = {};
      try {
        data = await res.json();
      } catch (_) {
        /* non-JSON body */
      }

      if (res.ok && data.ok) {
        actions.getAllUsers({ userId: catalog.user.userId });
        return { ok: true };
      }

      const error =
        data.message ||
        data.err ||
        data.error ||
        `Request failed (${res.status})`;
      return { ok: false, error };
    } catch (err) {
      return { ok: false, error: err.message || "ELO rebuild failed" };
    }
  };

  const handleInitiatePhoneVerification = async ({ userId }) => {
    return actions.initiateUserPhoneVerification({ userId, user });
  };

  const handleGrantMembership = async ({ userId, tier, durationDays }) => {
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/grant/adpass/${catalog.user.userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetUserId: userId,
            tier,
            durationDays,
          }),
        }
      );

      const data = await res.json();

      if (!data.ok) {
        return { ok: false, error: data.err || "Failed to grant membership" };
      }

      actions.getAllUsers({ userId: catalog.user.userId });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Failed to grant membership" };
    }
  };

  const handleRevokeMembership = async ({ userId }) => {
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/membership/revoke/adpass/${catalog.user.userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ targetUserId: userId }),
        }
      );

      const data = await res.json();

      if (!data.ok) {
        return { ok: false, error: data.err || "Failed to revoke membership" };
      }

      actions.getAllUsers({ userId: catalog.user.userId });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Failed to revoke membership" };
    }
  };

  const handleDeleteUser = async (targetUserId) => {
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/users/${targetUserId}/delete/adpass/${catalog.user.userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        return {
          ok: false,
          error: data.error || data.err || `Request failed (${res.status})`,
        };
      }

      actions.getAllUsers({ userId: catalog.user.userId });
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message || "Failed to delete user" };
    }
  };

  const getDaysDataForMatchesChart = (days) => {
    actions.getAdminDaysDataForMatchesChart({ user, days });
  };

  const getDaysDataForQueueChart = (days) => {
    actions.getAdminDaysDataForQueueChart({ user, days });
  };

  const getMonthlyDataForChart = () => {
    actions.getAdminDataForSignupsChart({ user, period: "month" });
  };

  const getYearlyDataForChart = () => {
    actions.getAdminDataForSignupsChart({ user, period: "year" });
  };

  return (
    <div className={classes.homepageRoot}>
      <SetELO
        openState={eloDialogState}
        userContext={adminUserContext}
        onClose={() => {
          actions.setELODialogState(false);
        }}
        onSetElo={(elo, userId) => {
          actions.updateUserELO({ elo, userId, user });
        }}
      />

      <SetPlayerBannedDialog
        openState={banPlayerDialogState}
        userContext={adminUserContext}
        onClose={() => {
          actions.setPlayerBannedDialogState(false);
        }}
        onSetPlayerBanned={(days, reason, userId) => {
          actions.updateUserAsBanned({ days, reason, userId, user });
        }}
      />

      <SetPlayerTrackerStatsDialog
        openState={trackerStatsDialogState}
        userContext={adminUserContext}
        onClose={() => {
          actions.setPlayerTrackerStatsDialogState(false);
        }}
        onSetPlayerStats={(stats, userId) => {
          actions.updateUserValveAndFaceitStats({ stats, userId, user });
        }}
      />

      <SetPlayerMembershipGrantDialog
        openState={membershipGrantDialogState}
        userContext={membershipGrantUserContext}
        onClose={() => {
          setMembershipGrantDialogState(false);
        }}
        onGrantMembership={handleGrantMembership}
      />

      <SetPlayerMembershipRevokeDialog
        openState={membershipRevokeDialogState}
        userContext={membershipRevokeUserContext}
        onClose={() => {
          setMembershipRevokeDialogState(false);
        }}
        onRevokeMembership={handleRevokeMembership}
      />

      <SetPlayerFounderBadgeDialog
        openState={founderBadgeDialogState}
        userContext={founderBadgeUserContext}
        adminUserId={catalog?.user?.userId}
        onClose={() => setFounderBadgeDialogState(false)}
        onComplete={() => actions.getAllUsers({ userId: catalog.user.userId })}
      />

      <RebuildPlayerEloDialog
        openState={rebuildPlayerEloDialogState}
        userContext={rebuildPlayerEloUserContext}
        onClose={() => {
          setRebuildPlayerEloDialogState(false);
        }}
        onConfirm={handleRebuildPlayerElo}
      />

      <IdentityReviewPanel
        openState={identityReviewDialogState}
        user={user}
        reviewUserContext={identityReviewUserContext}
        reviewData={identityReviewData}
        loading={identityReviewLoading}
        error={identityReviewError}
        onClose={closeIdentityReviewDialog}
        onRefresh={refreshIdentityReview}
        onConfirmReview={handleConfirmIdentityReview}
        onDismissMatch={handleDismissIdentityMatch}
        onRunIdentityScan={handleRunIdentityScan}
      />

      <SetPlayerNotEligibleDialog
        openState={notEligiblePlayerDialogState}
        userContext={adminUserContext}
        onClose={() => {
          actions.setPlayerNotEligibleDialogState(false);
        }}
        onSetPlayerNotEligible={(userId, reason) => {
          actions.updateSetUserAsNotEligible({ userId, reason, user });
        }}
      />

      <EligibilityThreadDialog
        open={eligibilityThreadDialogState}
        userContext={eligibilityThreadUserContext}
        adminUser={user}
        onClose={() => setEligibilityThreadDialogState(false)}
      />

      <SetPlayerVerifiedDialog
        openState={verifyPlayerDialogState}
        userContext={adminUserContext}
        onClose={() => {
          actions.setPlayerVerifiedDialogState(false);
        }}
        onSetPlayerVerified={(userId, options = {}) => {
          return actions.updateSetUserAsVerified({ userId, user, ...options });
        }}
      />

      <StartPhoneVerificationDialog
        openState={startPhoneVerificationDialogState}
        userContext={startPhoneVerificationUserContext}
        onClose={() => setStartPhoneVerificationDialogState(false)}
        onConfirm={handleInitiatePhoneVerification}
      />

      <SendApplicationFeedbackDialog
        openState={applicationFeedbackDialogState}
        userContext={applicationFeedbackUserContext}
        onClose={() => setApplicationFeedbackDialogState(false)}
        onSend={handleSendApplicationFeedback}
      />

      <ProfileReadinessReviewDialog
        openState={readinessReviewDialogState}
        userContext={readinessReviewUserContext}
        adminUserId={catalog?.user?.userId}
        edgeBasePath={basePath}
        onClose={() => setReadinessReviewDialogState(false)}
        onComplete={() => actions.getAllUsers({ userId: catalog.user.userId })}
      />

      <DeleteUserDialog
        openState={deleteUserDialogState}
        userContext={deleteUserContext}
        onClose={() => setDeleteUserDialogState(false)}
        onDeleteUser={handleDeleteUser}
      />

      <Paper className={classes.lobbyFrame}>
        {!user && <Typography>Please wait...</Typography>}

        <div className={classes.contentContainer}>
          <Paper className={classes.tabsContainer}>
            <Typography className={classes.pageTitle}>
              Ouro Matchmaking Admin
            </Typography>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              className={classes.tabsRoot}
              aria-label="manage data tabs"
            >
              {visibleTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  icon={tab.icon}
                  label={tab.label}
                  className={classes.tabRoot}
                  id={`manage-tab-${tab.key}`}
                  aria-controls={`manage-tabpanel-${tab.key}`}
                />
              ))}
            </Tabs>
          </Paper>

          {tabValue === "monitoring" && (
            <div className={classes.containerContentTab}>
              <MonitoringSection 
                user={user}
                mmActivitySummary={mmActivitySummary}
                newestPlayers={newestPlayers}
                queueAnalitycsSummary={queueAnalitycsSummary}
                getDaysDataForMatchesChart={getDaysDataForMatchesChart}
                getDaysDataForQueueChart={getDaysDataForQueueChart}
                getMonthlyDataForChart={getMonthlyDataForChart}
                getYearlyDataForChart={getYearlyDataForChart}
                sevenDaysMatchmakingDataForChart={sevenDaysMatchmakingDataForChart}
                thirtyDaysMatchmakingDataForChart={thirtyDaysMatchmakingDataForChart}
                ninetyDaysMatchmakingDataForChart={ninetyDaysMatchmakingDataForChart}
                sevenDaysQueueDataForChart={sevenDaysQueueDataForChart}
                thirtyDaysQueueDataForChart={thirtyDaysQueueDataForChart}
                ninetyDaysQueueDataForChart={ninetyDaysQueueDataForChart}
                weeklyDataForChart={weeklyDataForChart}
                monthlyDataForChart={monthlyDataForChart}
                yearlyDataForChart={yearlyDataForChart}
                matchDurationData={matchDurationData}
                playersQueuedPerDayData={playersQueuedPerDayData}
                queueEfficiencyPerDayData={queueEfficiencyPerDayData}
                queueOutcomeDistributionData={queueOutcomeDistributionData}
                matchmakingCapacityTrendData={matchmakingCapacityTrendData}
                getMatchmakingCapacityTrendData={(days) =>
                  actions.getAdminMatchmakingCapacityTrendData({ user, days })
                }
                serversAliveCount={serversAliveCount}
              />
            </div>
          )}

          {tabValue === "user-management" && (
            <div className={classes.containerContentTab}>
              <UsersTable
                canManageAdvancedUserTools={canUseAdvancedUserTools}
                openSetEloDialog={openSetEloDialog}
                openRebuildPlayerEloDialog={openRebuildPlayerEloDialog}
                openSetAsVerifiedDialog={openSetAsVerifiedDialog}
                openStartPhoneVerificationDialog={openStartPhoneVerificationDialog}
                openSetAsNotEligibleDialog={openSetAsNotEligibleDialog}
                openSetAsBannedDialog={openSetAsBannedDialog}
                openSetAsTrackerStatsDialog={openSetAsTrackerStatsDialog}
                openSetMembershipGrantDialog={openSetMembershipGrantDialog}
                openSetMembershipRevokeDialog={openSetMembershipRevokeDialog}
                openFounderBadgeDialog={openFounderBadgeDialog}
                openIdentityReviewDialog={openIdentityReviewDialog}
                openReputationOutcomesPage={openReputationOutcomesPage}
                openSendApplicationFeedbackDialog={openSendApplicationFeedbackDialog}
                openEligibilityThreadDialog={openEligibilityThreadDialog}
                openReadinessReviewDialog={openReadinessReviewDialog}
                openDeleteUserDialog={openDeleteUserDialog}
                rows={users}
                user={user}
              />
            </div>
          )}

          {tabValue === "reported-players" && (
            <div className={classes.containerContentTab}>
              <ReportedPlayerTable rows={reportedPlayers} />
            </div>
          )}

          {tabValue === "support-tickets" && (
            <div className={classes.containerContentTab}>
              <SupportTicketsPage catalog={catalog} />
            </div>
          )}

        </div>
      </Paper>
    </div>
  );
}

UsersPage.propTypes = {
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  catalog: PropTypes.object.isRequired,
};

export default withStyles(styles)(UsersPage);
